
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import TailwindLayout from 'TailwindcssComponents/TailwindLayout';
import TopBar from 'TailwindcssComponents/TopBar';
import DataTable from 'TailwindcssComponents/Tables';
import axios from 'axios';
import FooterTailwind from 'TailwindcssComponents/Footer';
import DeleteDialog from 'components/DeleteDialogBox/deleteDialog';
import { ToastContainer, toast } from 'react-toastify';
import { UserDataContext } from 'context';

export default function Venues() {
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const bearerToken = localStorage.getItem('token');
    const { userData, selectedOrganization } = useContext(UserDataContext);
    const userRole = userData?.isAdmin ? 'admin' : selectedOrganization?.name || 'user';
    const orgId = selectedOrganization?.organization?.id || null;
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [venuesData, setVenuesData] = useState([]);
    const [openDeleteDialogBox, setOpenDeleteDialogBox] = useState();
    const [selectedVenueId, setSelectedVenueId] = useState(null);
    const [totalItemsCount, setTotalItemsCount] = useState(0);
    const [totalPagesCount, setTotalPagesCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [venueStatus, setVenueStatus] = useState(null);
    const [eventCategories, setEventCategories] = useState(null);
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);
    const [selectedCategoryName, setSelectedCategoryName] = useState();
    const [selectedDate, setSelectedDate] = useState(null);
    const [filteredVenues, setFilteredVenues] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isArchived, setIsArchived] = useState(false);
    const isAdmin = userRole === 'admin';


    const fetchVenues = async () => {
        const params = {
            page: currentPage,
            limit: rowsPerPage,
            name: searchTerm,
            ...(isArchived !== null ? { isActive: !isArchived } : {})
        };

        try {
            setIsLoading(true);
            const response = await axios.get(`${apiUrl}event-service/event-venue`, { params });
            if (response.data?.success) {
                setVenuesData(response.data.data);
                setFilteredVenues(response.data.data);
                setTotalItemsCount(response.data.totalItems || 0);
                setTotalPagesCount(response.data.totalPages || 0);
            } else throw new Error(response.data?.message || 'Error in fetching venues');
        } catch (error) {
            console.error(error.response?.data?.message || error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleActionClick = (id, action) => {
        const actions = {
            view: () => navigate(`/venues/single-venue/${id}`),
            edit: () => navigate(`/venues/edit-venue/${id}`),
            delete: () => handleDelete(id),
            archive: () => handleArchive(id, true),
            unarchive: () => handleArchive(id, false),
        };
        actions[action]?.();
    };

    const handleArchive = async (id, isArchived) => {
        try {
            const url = `${apiUrl}event-service/event-venue/${isArchived ? 'deactive' : 'active'}/${id}`;

            const response = await axios.patch(url, {}, {
                headers: { 'Authorization': `Bearer ${bearerToken}` }
            });

            if (response?.data.success) {
                toast.warning(response.data.message || `Venue has been ${isArchived ? 'deactivated' : 'activated'}`);
                fetchVenues();
            } else {
                throw new Error(response?.data.message || 'Unable to update');
            }
        } catch (error) {
            const errorMsg = error.response?.data.message || error.message;
            console.error(errorMsg);
            toast.error(errorMsg);
        }
    };

    const handleDelete = async (id) => {
        setSelectedVenueId(id);
        setOpenDeleteDialogBox(true);
    };

    const closeDeleteDialogBox = () => {
        setOpenDeleteDialogBox(false);
    };

    const handleDeleteConfirm = async () => {
        try {
            setIsLoading(true);
            const response = await axios.delete(`${apiUrl}event-service/event-venue/${selectedVenueId}`,
                {
                    headers: { 'Authorization': `Bearer ${bearerToken}` }
                });
            if (response?.data.success) {
                toast.warning(response?.data.message || 'Event has been deleted');
            }
            else throw new Error(response?.data.message || 'Unable to delete');
        } catch (error) {
            console.error(error.response?.data.message || error.message);
            toast.error(error.response?.data.message || error.message);
        } finally {
            fetchVenues();
            setOpenDeleteDialogBox(false);
            setIsLoading(false);
        }
    };

    const filterVenues = (term) => {
        if (!term) {
            setFilteredVenues(venuesData); // Reset to all data if search term is empty
            return;
        }
        const lowerCaseTerm = term.toLowerCase();
        const filtered = venuesData.filter((venue) =>
            venue.name.toLowerCase().includes(lowerCaseTerm) || // Filter by name
            venue.description?.toLowerCase().includes(lowerCaseTerm) // Optional: Filter by description
        );
        setFilteredVenues(filtered);
    };

    const handleSearch = (term) => {
        setSearchTerm(term);
        filterVenues(term);
    };

    useEffect(() => {
        fetchVenues();
        // if (selectedCategoryId && eventCategories) {
        //     const selectedCategory = eventCategories?.find((category) => category.id === selectedCategoryId);
        //     setSelectedCategoryName(selectedCategory ? selectedCategory.name : null);
        // }
    }, [currentPage, rowsPerPage, venueStatus, selectedDate, searchTerm, bearerToken, isArchived]);

    const formattedDate = (date) => {
        const options = { day: '2-digit', month: 'short', year: 'numeric' };
        return new Date(date).toLocaleDateString('en-GB', options);
    };

    const columns = [
        {
            key: 'name', label: 'Venue Name',
            render: (row) => (row.name),
        },
        { key: 'city', label: 'City', render: (row) => (row.venueCity?.name || row.city) },
        { key: 'ownerName', label: 'Owner Name', render: (row) => (row.ownerName) },
        { key: 'ownerPhone', label: 'Owner Phone', render: (row) => (row.ownerPhone) },
        {
            key: 'status',
            label: 'Status',
            render: (row) => (
                <div
                    className={`border rounded ${row.isActive ? 'bg-[#45B369] bg-opacity-15 text-[#45B369] border-[#45B369]' : 'bg-red-100 text-red-700 border-red-500'} w-fit h-7 flex items-center`}
                >
                    <div className="py-1 px-3">{row.isActive ? 'Active' : 'Inactive'}</div>
                </div>
            ),
        },
    ];

    return (
        <TailwindLayout>
            <TopBar />
            <div className="p-12 bg-gray-100 min-h-screen">
                <DataTable
                    title={'Venues'}
                    rows={filteredVenues}
                    columns={columns}
                    onActionClick={handleActionClick}
                    options={{ showHeader: true, showHeaderTwo: false, viewOption: true, editOption: true, addoption: false, statusFilterOption: false, dateFilterOption: false, typeFilterOption: false, searchOption: true, archiveFilterOption: true }}
                    totalCount={totalItemsCount}
                    pagesCount={totalPagesCount}
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(limit) => setRowsPerPage(limit)}
                    onPageChange={(page) => setCurrentPage(page)}
                    venueStatus={venueStatus}
                    onStatusChange={(status) => setVenueStatus(status)}
                    categories={eventCategories}
                    onCategoryChange={(id) => setSelectedCategoryId(id)}
                    category={selectedCategoryName}
                    onDateChange={(date) => setSelectedDate(date)}
                    filteredDate={selectedDate}
                    onSearch={handleSearch}
                    searcItem={searchTerm}
                    role={userRole}
                    loaderStatus={isLoading}
                    userRole={userRole}
                    isArchivedStatus={isArchived}
                    onToggle={(value) => setIsArchived(value)}
                    addButtonOnlyOption={isAdmin}

                    addButtonTitle={"Add Venue"}
                    handleDialogBoxes={() => navigate("/add-venue")}
                />
            </div>
            <DeleteDialog
                open={openDeleteDialogBox}
                onClose={closeDeleteDialogBox}
                onDelete={handleDeleteConfirm}
                name={'venue'}
            />
            <ToastContainer
                position="bottom-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
            <FooterTailwind />
        </TailwindLayout>
    )
}